import {
  type FileInfo,
  LETTER_ACTIONS,
  LETTER_DIRECTIONS,
  LETTER_STATUSES,
  type LetterAction,
  type LetterDetail,
  type LetterDirection,
  type LetterStatus,
  type LetterSummary,
  type LetterTermSuggestion,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { FieldBasis } from '../../components/FieldBasis';
import { FileLink } from '../../components/FileLink';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog, Sheet } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import { KeyValue, Page, PageHeader } from '../../ui/layout';
import { ProjectFilter, useProjectDefault, useProjectsWith } from '../psd/shared';
import { uploadSupportFiles } from '../support/SupportDialogs';

type Search = {
  letter?: string;
  project?: string;
  direction?: LetterDirection;
  status?: string;
  mine?: boolean;
  overdue?: boolean;
  q?: string;
};

const STATUS_TONE: Record<LetterStatus, 'neutral' | 'info' | 'success'> = {
  registered: 'neutral',
  in_work: 'info',
  answered: 'success',
  closed: 'neutral',
};
const today = () => new Date().toLocaleDateString('en-CA');

/** Реестр писем: входящие и исходящие одним списком, срок ответа, ответственный, просрочка (ДК-01, ДК-02) */
export function LettersPage() {
  const search = useSearch({ strict: false }) as Search;
  const navigate = useNavigate();
  useCrumbs([{ label: 'Письма' }]);
  const [creating, setCreating] = useState(false);
  const go = (patch: Partial<Search>) =>
    navigate({ to: '/documents/letters', search: { ...search, ...patch } as never, replace: true });
  const q = useQuery({
    queryKey: [
      'letters',
      'list',
      search.project,
      search.direction,
      search.status,
      search.mine,
      search.overdue,
      search.q,
    ],
    queryFn: () =>
      api<LetterSummary[]>('/documents/letters', {
        query: {
          projectId: search.project,
          direction: search.direction,
          status: search.status,
          mine: search.mine || undefined,
          overdue: search.overdue || undefined,
          q: search.q || undefined,
        },
      }),
  });
  const { list: writable } = useProjectsWith('documents.edit');
  return (
    <Page>
      <PageHeader
        title="Письма"
        subtitle="Входящие и исходящие одним списком: номер без пропусков, срок ответа, ответственный"
        actions={
          writable.length ? (
            <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
              Зарегистрировать письмо
            </Button>
          ) : null
        }
      />
      <div className="mb-4 grid gap-3 md:grid-cols-[auto_auto_auto_1fr] md:items-center">
        <ProjectFilter
          value={search.project}
          onChange={(v) => go({ project: v })}
          perms={['documents.view']}
        />
        <Select
          aria-label="Направление"
          className="h-11 md:h-10 md:w-44"
          value={search.direction ?? ''}
          onChange={(e) => go({ direction: (e.target.value || undefined) as LetterDirection | undefined })}
        >
          <option value="">Все письма</option>
          <option value="in">Входящие</option>
          <option value="out">Исходящие</option>
        </Select>
        <Select
          aria-label="Состояние"
          className="h-11 md:h-10 md:w-52"
          value={search.status ?? ''}
          onChange={(e) => go({ status: e.target.value || undefined })}
        >
          <option value="">Любое состояние</option>
          <option value="open">Нужно что-то сделать</option>
          {(Object.keys(LETTER_STATUSES) as LetterStatus[]).map((s) => (
            <option key={s} value={s}>
              {LETTER_STATUSES[s]}
            </option>
          ))}
        </Select>
        <Input
          aria-label="Поиск по теме, корреспонденту, номеру"
          placeholder="Поиск по теме, корреспонденту, номеру"
          defaultValue={search.q ?? ''}
          className="h-11 md:h-10"
          onKeyDown={(e) => {
            if (e.key === 'Enter') go({ q: e.currentTarget.value.trim() || undefined });
          }}
          onBlur={(e) => go({ q: e.currentTarget.value.trim() || undefined })}
        />
      </div>
      <div className="mb-4 flex flex-wrap gap-x-6">
        <Checkbox
          className="min-h-11 items-center"
          label="Только мои"
          checked={Boolean(search.mine)}
          onChange={(e) => go({ mine: e.target.checked || undefined })}
        />
        <Checkbox
          className="min-h-11 items-center"
          label="Просроченные"
          checked={Boolean(search.overdue)}
          onChange={(e) => go({ overdue: e.target.checked || undefined })}
        />
      </div>
      {q.isPending ? <Spinner /> : null}
      <ErrorBox error={q.error} />
      {q.data && !q.data.length ? (
        <EmptyState title="Писем нет">Зарегистрированные письма появятся здесь.</EmptyState>
      ) : null}
      <ul className="flex flex-col gap-2">
        {q.data?.map((l) => (
          <li key={l.id}>
            <button
              type="button"
              onClick={() => go({ letter: l.id })}
              className="flex min-h-11 w-full flex-col gap-1 rounded-lg border border-line bg-surface px-4 py-3 text-left hover:border-ink-3"
            >
              <span className="flex flex-wrap items-center gap-2">
                <span className="text-[14px] font-semibold text-ink">{l.number}</span>
                <Badge tone={STATUS_TONE[l.status]}>{LETTER_STATUSES[l.status]}</Badge>
                {l.overdue ? <Badge tone="danger">Просрочен ответ</Badge> : null}
                <span className="text-[12px] text-ink-2">
                  {l.projectCode} · {date(l.letterDate)}
                </span>
              </span>
              <span className="text-[14px] text-ink [overflow-wrap:anywhere]">{l.subject}</span>
              <span className="text-[12px] text-ink-2">
                {LETTER_DIRECTIONS[l.direction]}
                {l.correspondent ? ` · ${l.correspondent}` : ''}
                {l.needsReply && l.replyDue ? ` · ответ до ${date(l.replyDue)}` : ''}
                {l.responsibleName ? ` · ${l.responsibleName}` : ''}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {creating ? <NewLetter defaultProject={search.project} onClose={() => setCreating(false)} /> : null}
      {search.letter ? <LetterSheet id={search.letter} onClose={() => go({ letter: undefined })} /> : null}
    </Page>
  );
}

type ReplyTo = {
  id: string;
  projectId: string;
  direction: LetterDirection;
  correspondent: string;
  subject: string;
};

function NewLetter({
  defaultProject,
  replyTo,
  onClose,
}: {
  defaultProject?: string;
  replyTo?: ReplyTo;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { list } = useProjectsWith('documents.edit');
  const [f, setF] = useState({
    projectId: replyTo?.projectId ?? '',
    direction: (replyTo ? (replyTo.direction === 'in' ? 'out' : 'in') : 'in') as LetterDirection,
    correspondentName: replyTo?.correspondent ?? '',
    subject: replyTo ? `Ответ: ${replyTo.subject}` : '',
    summary: '',
    letterDate: today(),
    externalNumber: '',
    needsReply: false,
    replyDue: '',
  });
  const [files, setFiles] = useState<File[]>([]);
  const input = useRef<HTMLInputElement>(null);
  const setProject = useCallback((id: string) => setF((x) => ({ ...x, projectId: id })), []);
  useProjectDefault(list, defaultProject, f.projectId, setProject);
  const send = useMutation({
    mutationFn: async () => {
      const l = await api<LetterDetail>('/documents/letters', {
        method: 'POST',
        body: {
          projectId: f.projectId,
          direction: f.direction,
          correspondentName: f.correspondentName.trim() || null,
          subject: f.subject.trim(),
          summary: f.summary.trim() || null,
          letterDate: f.letterDate,
          externalNumber: f.externalNumber.trim() || null,
          needsReply: f.needsReply,
          replyDue: f.needsReply ? f.replyDue || null : null,
          replyToId: replyTo?.id ?? null,
        },
      });
      if (files.length) await uploadSupportFiles('letter', l.id, files);
      return l;
    },
    onSuccess: (l) => {
      qc.invalidateQueries({ queryKey: ['letters'] });
      qc.invalidateQueries({ queryKey: ['inbox'] });
      toast(`Письмо зарегистрировано: ${l.number}`);
      onClose();
    },
  });
  const hint = useMutation({
    mutationFn: () =>
      api<{ suggestion: LetterTermSuggestion | null }>('/documents/letters/term-hint', {
        method: 'POST',
        body: { text: `${f.subject} ${f.summary}`.trim(), letterDate: f.letterDate },
      }),
  });
  const resetHint = hint.reset;
  const ready =
    Boolean(f.projectId) &&
    f.subject.trim().length >= 3 &&
    f.correspondentName.trim().length > 0 &&
    (!f.needsReply || Boolean(f.replyDue));
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Регистрация письма"
      description="Номер присваивается автоматически, без пропусков"
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button variant="primary" disabled={!ready} loading={send.isPending} onClick={() => send.mutate()}>
            Зарегистрировать
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <ErrorBox error={send.error} />
        <ErrorBox error={hint.error} />
        <Field label="Проект" htmlFor="lt-project">
          <Select
            id="lt-project"
            value={f.projectId}
            onChange={(e) => setF({ ...f, projectId: e.target.value })}
          >
            {list.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Направление" htmlFor="lt-dir">
          <Select
            id="lt-dir"
            value={f.direction}
            onChange={(e) => setF({ ...f, direction: e.target.value as LetterDirection })}
          >
            <option value="in">Входящее</option>
            <option value="out">Исходящее</option>
          </Select>
        </Field>
        <Field label={f.direction === 'in' ? 'От кого' : 'Кому'} htmlFor="lt-who">
          <Input
            id="lt-who"
            value={f.correspondentName}
            onChange={(e) => setF({ ...f, correspondentName: e.target.value })}
          />
        </Field>
        <Field label="Тема" htmlFor="lt-subject">
          <Input
            id="lt-subject"
            value={f.subject}
            maxLength={500}
            onChange={(e) => setF({ ...f, subject: e.target.value })}
          />
        </Field>
        <Field label="Кратко о содержании" htmlFor="lt-summary">
          <Textarea
            id="lt-summary"
            value={f.summary}
            onChange={(e) => setF({ ...f, summary: e.target.value })}
          />
        </Field>
        <Field label="Дата письма" htmlFor="lt-date">
          <Input
            id="lt-date"
            type="date"
            value={f.letterDate}
            onChange={(e) => {
              resetHint();
              setF({ ...f, letterDate: e.target.value });
            }}
          />
        </Field>
        <Field label="Номер на самом письме" htmlFor="lt-ext">
          <Input
            id="lt-ext"
            value={f.externalNumber}
            onChange={(e) => setF({ ...f, externalNumber: e.target.value })}
          />
        </Field>
        <Checkbox
          className="min-h-11 items-center"
          label="Нужен ответ"
          checked={f.needsReply}
          onChange={(e) => setF({ ...f, needsReply: e.target.checked })}
        />
        {f.needsReply ? (
          <>
            <Field label="Срок ответа" htmlFor="lt-due">
              <Input
                id="lt-due"
                type="date"
                value={f.replyDue}
                onChange={(e) => setF({ ...f, replyDue: e.target.value })}
              />
            </Field>
            <div className="flex flex-col gap-2">
              <Button
                className="min-h-11 self-start"
                loading={hint.isPending}
                onClick={() => hint.mutate()}
                disabled={`${f.subject}${f.summary}`.trim().length < 3}
              >
                Найти срок в тексте письма
              </Button>
              {hint.data ? (
                hint.data.suggestion ? (
                  <div className="flex flex-wrap items-center gap-2 text-[13px] text-ink-2">
                    <span>
                      В тексте: «{hint.data.suggestion.quote}» → до {date(hint.data.suggestion.date)}
                      {hint.data.suggestion.kind === 'working_days' ? ' (рабочие дни по календарю РК)' : ''}
                    </span>
                    <Button
                      className="min-h-11"
                      onClick={() => setF({ ...f, replyDue: hint.data!.suggestion!.date })}
                    >
                      Поставить этот срок
                    </Button>
                  </div>
                ) : (
                  <span className="text-[13px] text-ink-2">
                    В теме и кратком содержании срок не найден — укажите вручную
                  </span>
                )
              ) : null}
            </div>
            <FieldBasis field="letter.response_due" />
          </>
        ) : null}
        <Field label="Скан или файл письма">
          <input
            ref={input}
            type="file"
            multiple
            accept="image/*,.pdf,.docx,.xlsx"
            className="hidden"
            onChange={(e) => setFiles(Array.from(e.target.files ?? []).slice(0, 5))}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => input.current?.click()}>Выбрать файлы</Button>
            {files.length ? <span className="text-[13px] text-ink-2">Выбрано: {files.length}</span> : null}
          </div>
        </Field>
      </div>
    </Dialog>
  );
}

function LetterSheet({ id, onClose }: { id: string; onClose: () => void }) {
  return <LetterSheetBody key={id} id={id} onClose={onClose} />;
}

function LetterSheetBody({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [closing, setClosing] = useState(false);
  const [replying, setReplying] = useState(false);
  const q = useQuery({
    queryKey: ['letters', 'one', id],
    queryFn: () => api<LetterDetail>(`/documents/letters/${id}`),
  });
  const files = useQuery({
    queryKey: ['letters', 'files', id],
    queryFn: () => api<FileInfo[]>('/files', { query: { entityType: 'letter', entityId: id } }),
  });
  const act = useMutation({
    mutationFn: (action: LetterAction) =>
      api<LetterDetail>(`/documents/letters/${id}/action`, {
        method: 'POST',
        body: { action, note: note.trim() || null },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['letters'] });
      qc.invalidateQueries({ queryKey: ['inbox'] });
      setClosing(false);
      setNote('');
    },
  });
  const l = q.data;
  return (
    <Sheet
      open
      onOpenChange={(o) => !o && onClose()}
      title={l ? `${l.number}` : 'Письмо'}
      description={l?.projectCode}
    >
      {q.isPending ? <Spinner /> : null}
      <ErrorBox error={q.error} />
      {l ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={STATUS_TONE[l.status]}>{LETTER_STATUSES[l.status]}</Badge>
            {l.overdue ? <Badge tone="danger">Просрочен ответ</Badge> : null}
          </div>
          <p className="text-[15px] font-medium text-ink [overflow-wrap:anywhere]">{l.subject}</p>
          {l.summary ? <p className="text-[14px] whitespace-pre-line text-ink-2">{l.summary}</p> : null}
          <KeyValue
            items={[
              { label: LETTER_DIRECTIONS[l.direction], value: l.correspondent ?? '—' },
              { label: 'Дата письма', value: date(l.letterDate) },
              { label: 'Номер на письме', value: l.externalNumber ?? '—' },
              { label: 'Ответственный', value: l.responsibleName ?? '—' },
              { label: 'Срок ответа', value: l.needsReply && l.replyDue ? date(l.replyDue) : 'не требуется' },
              ...(l.contractNumber ? [{ label: 'Договор', value: l.contractNumber }] : []),
              ...(l.remarkNumber ? [{ label: 'Предписание', value: l.remarkNumber }] : []),
              ...(l.changeTitle ? [{ label: 'Запрос на изменение', value: l.changeTitle }] : []),
              ...(l.replyToNumber ? [{ label: 'В ответ на', value: l.replyToNumber }] : []),
              ...(l.answerLetterNumber ? [{ label: 'Ответ', value: l.answerLetterNumber }] : []),
              ...(l.answeredAt ? [{ label: 'Ответ дан', value: dateTime(l.answeredAt) }] : []),
              ...(l.closeNote ? [{ label: 'Причина закрытия', value: l.closeNote }] : []),
            ]}
          />
          <div>
            <h3 className="mb-1 text-[13px] font-semibold text-ink-2">Файлы</h3>
            {files.data?.length ? (
              <ul className="flex flex-col gap-1.5 text-[14px]">
                {files.data.map((f) => (
                  <li key={f.id}>
                    <FileLink file={f} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[13px] text-ink-3">Файлов нет</p>
            )}
          </div>
          <ErrorBox error={act.error} />
          {closing ? (
            <Field label="Причина закрытия без ответа" htmlFor="lt-note">
              <Textarea id="lt-note" value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {l.actions.includes('answer') ? (
              <Button className="min-h-11" onClick={() => setReplying(true)}>
                Ответить письмом
              </Button>
            ) : null}
            {l.actions.map((a) =>
              a === 'close' && !closing ? (
                <Button key={a} onClick={() => setClosing(true)}>
                  {LETTER_ACTIONS[a]}
                </Button>
              ) : (
                <Button
                  key={a}
                  variant={a === 'start' || a === 'answer' ? 'primary' : 'secondary'}
                  loading={act.isPending}
                  disabled={a === 'close' && note.trim().length < 3}
                  onClick={() => act.mutate(a)}
                >
                  {LETTER_ACTIONS[a]}
                </Button>
              ),
            )}
          </div>
        </div>
      ) : null}
      {replying && l ? (
        <NewLetter
          replyTo={{
            id: l.id,
            projectId: l.projectId,
            direction: l.direction,
            correspondent: l.correspondent ?? '',
            subject: l.subject,
          }}
          onClose={() => {
            setReplying(false);
            qc.invalidateQueries({ queryKey: ['letters'] });
          }}
        />
      ) : null}
    </Sheet>
  );
}
